import { ComponentClass, PureComponent, ReactNode } from 'react';

import { FormattedMessage, injectIntl, InjectedIntl } from 'react-intl';

import Button from '@atlaskit/button/standard-button';
import BranchIcon from '@atlaskit/icon/glyph/bitbucket/branches';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import { Popup, TriggerProps } from '@atlaskit/popup';
import Tooltip from '@atlaskit/tooltip';

import { ScreenReadersOnly } from 'src/components/accessibility';
import { Repository } from 'src/components/types';
import { Ref } from 'src/sections/repository/types';

import {
  InlineDialogWrapper,
  RefSelectorWrapper,
  PopupContentWrapper,
} from '../ref-selector/ref-selector.style';

import RefSelectorPanel from './ref-selector-panel';
import messages from './ref-selector.i18n';

type RefSelectorProps = {
  label?: ReactNode;
  onChange?: (ref: Ref) => void;
  onOpen?: () => void;
  renderRef?: (ref: Ref) => any;
  repository: Repository;
};

type InjectedProps = {
  intl: InjectedIntl;
};

type RefSelectorState = {
  isOpen: boolean;
};

const defaultProps = {
  onChange: () => {},
  onOpen: () => {},
};

type DefaultProps = typeof defaultProps;

class RefSelector extends PureComponent<
  RefSelectorProps & DefaultProps & InjectedProps,
  RefSelectorState
> {
  static defaultProps = defaultProps;

  state = {
    isOpen: false,
  };

  handleClose = () => this.setState({ isOpen: false });

  handleRefSelected = (ref: Ref) => {
    this.setState({ isOpen: false });
    this.props.onChange(ref);
  };

  toggle() {
    if (!this.state.isOpen) {
      this.props.onOpen();
    }

    this.setState((prevState: RefSelectorState) => ({
      isOpen: !prevState.isOpen,
    }));
  }

  renderTrigger = (triggerProps: TriggerProps) => {
    const { isOpen } = this.state;
    const { intl, label } = this.props;

    const buttonText = label || (
      <FormattedMessage {...messages.noRefSelected} />
    );

    return (
      <Tooltip content={buttonText} tag="p" position="top">
        <Button
          {...triggerProps}
          isSelected={isOpen}
          iconBefore={<BranchIcon size="medium" label="" />}
          iconAfter={<ChevronDownIcon label="" />}
          testId="ref-selector-trigger"
          onClick={() => {
            this.toggle();
          }}
        >
          <ScreenReadersOnly>
            {intl.formatMessage(messages.refSourceLabel)}
          </ScreenReadersOnly>
          {buttonText}
        </Button>
      </Tooltip>
    );
  };

  render() {
    const { renderRef, repository } = this.props;
    const { isOpen } = this.state;

    return (
      <RefSelectorWrapper data-qa="ref-selector">
        <InlineDialogWrapper>
          <Popup
            isOpen={isOpen}
            onClose={this.handleClose}
            placement="bottom-start"
            trigger={this.renderTrigger}
            content={({ setInitialFocusRef }) => (
              <PopupContentWrapper>
                <RefSelectorPanel
                  onRefSelected={this.handleRefSelected}
                  renderRef={renderRef}
                  repository={repository}
                  setInitialFocusRef={setInitialFocusRef}
                />
              </PopupContentWrapper>
            )}
          />
        </InlineDialogWrapper>
      </RefSelectorWrapper>
    );
  }
}

export default injectIntl(RefSelector) as ComponentClass<RefSelectorProps>;
